class DynamicMediaGallery {
  constructor(wrapper) {
    this.wrapper = wrapper;
    this.gallery = wrapper.querySelector('[data-gallery]');
    this.thumbGrid = wrapper.querySelector('[data-thumbs]');

    if (!this.gallery) return;

    this.layout = this.gallery.dataset.layout;
    this.hideVariants = wrapper.dataset.hideVariants === 'true';
    this.activeIndex = 0;
    this.currentVariantId = null;
    this.productData = null;

    this.mainItems = Array.from(this.gallery.querySelectorAll('.ceps-main-media-item'));
    this.thumbItems = Array.from(wrapper.querySelectorAll('.ceps-thumb-btn'));
    this.prevBtn = wrapper.querySelector('.ceps-gallery-prev');
    this.nextBtn = wrapper.querySelector('.ceps-gallery-next');
    this.counterCurrent = wrapper.querySelector('.ceps-gallery-counter .current');
    this.counterTotal = wrapper.querySelector('.ceps-gallery-counter .total');

    this.thumbLookup = new Map(this.thumbItems.map(btn => [String(btn.dataset.mediaId), btn]));
    this.scrollObserver = null;
    this.isScrolling = false;

    this.init();
  }

  init() {
    const jsonEl = this.wrapper.querySelector('[id^="ProductJson-"]') || this.wrapper.querySelector('.product-json-response');
    if (jsonEl) {
      try {
        this.productData = JSON.parse(jsonEl.textContent);
      } catch (e) {}
    }

    this.bindGalleryEvents();
    this.bindVariantChangeEvents();
    this.bindMobileScroll();

    const initialVariantId = this.getVariantIdFromFormOrUrl() || this.wrapper.dataset.currentVariant;
    if (initialVariantId) {
      requestAnimationFrame(() => this.updateGallery(initialVariantId));
    } else {
      this.showImage(0);
    }
  }

  getVariantIdFromFormOrUrl() {
    const form = this.wrapper.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const input = form.querySelector('input[name="id"], select[name="id"], .main-variant');
      if (input && input.value) return input.value;
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('variant')) {
      return urlParams.get('variant');
    }
    return null;
  }

  bindGalleryEvents() {
    this.prevBtn?.addEventListener('click', () => this.showImage(this.activeIndex - 1));
    this.nextBtn?.addEventListener('click', () => this.showImage(this.activeIndex + 1));

    this.wrapper.addEventListener('click', (e) => {
      const thumb = e.target.closest('.ceps-thumb-btn');
      if (!thumb) return;
      e.preventDefault();
      const visibleItems = this.getVisibleItems();
      const index = visibleItems.findIndex(item => item.dataset.mediaId === thumb.dataset.mediaId);
      if (index >= 0) this.showImage(index);
    });
  }

  bindVariantChangeEvents() {
    const handleVariantUpdate = () => {
      setTimeout(() => {
        const id = this.getVariantIdFromFormOrUrl();
        if (id && String(id) !== String(this.currentVariantId)) {
          this.updateGallery(id);
        }
      }, 50);
    };

    document.addEventListener('change', (e) => {
      const target = e.target;
      if (target.name === 'id' || target.closest('variant-selects, variant-radios, .variant-radio')) {
        handleVariantUpdate();
      }
    });

    const originalPushState = history.pushState;
    history.pushState = function() {
      originalPushState.apply(this, arguments);
      handleVariantUpdate();
    };

    const originalReplaceState = history.replaceState;
    history.replaceState = function() {
      originalReplaceState.apply(this, arguments);
      handleVariantUpdate();
    };

    window.addEventListener('popstate', handleVariantUpdate);
  }

  updateVariantTitle(variantId) {
    const titleWrapper = this.wrapper.querySelector('.product-single__title');
    if (titleWrapper?.dataset.enableVariantTitle !== 'true') return;
    const titleSpan = titleWrapper.querySelector('.product-variant-title');
    if (!titleSpan) return;

    if (this.productData && this.productData.variants) {
      const variantData = this.productData.variants.find(v => String(v.id) === String(variantId));
      if (variantData) {
        titleSpan.textContent = variantData.title;
      }
    }
  }

// UPDATED: Forces hiding and cross-references main image data if thumbnails lack the attribute
  _isVariantVisible(element, variantId) {
    let attachedVariantIds = element.dataset.variantIds;

    // If this is a thumbnail and it's missing the variant ID data, look up its matching main image
    if (element.classList.contains('ceps-thumb-btn') && (!attachedVariantIds || attachedVariantIds.trim() === '')) {
      const mediaId = element.dataset.mediaId;
      const matchingMainItem = this.gallery.querySelector(`.ceps-main-media-item[data-media-id="${mediaId}"]`);
      if (matchingMainItem) {
        attachedVariantIds = matchingMainItem.dataset.variantIds;
      }
    }

    // If neither the thumb nor main item has attached variants, it's a general image. Show it.
    if (!attachedVariantIds || attachedVariantIds.trim() === '') return true;

    // Clean up the string and check for an exact match (handles formatting inconsistencies)
    const idArray = attachedVariantIds.split(',').map(id => id.trim()).filter(Boolean);

    // If the current variant ID is in the list, show it. Otherwise, hide it immediately.
    return idArray.includes(String(variantId));
  }

  // UPDATED: Physically moves the active variant image to position #1
  updateGallery(variantId) {
    this.currentVariantId = String(variantId);
    this.updateVariantTitle(this.currentVariantId);

    // 1. Find the specific main item and thumb item for this variant
    let specificMainItem = null;
    let specificThumbBtn = null;

    this.mainItems.forEach(item => {
      const vIds = item.dataset.variantIds;
      if (vIds && vIds.includes(this.currentVariantId + ',')) specificMainItem = item;
    });

    this.thumbItems.forEach(btn => {
      const vIds = btn.dataset.variantIds;
      if (vIds && vIds.includes(this.currentVariantId + ',')) specificThumbBtn = btn;
    });

    // 2. Physically move them to the front of the DOM and internal arrays
    if (specificMainItem && this.gallery) {
      this.gallery.prepend(specificMainItem);
      this.mainItems = this.mainItems.filter(i => i !== specificMainItem);
      this.mainItems.unshift(specificMainItem);
    }

    if (specificThumbBtn) {
      const thumbWrapper = specificThumbBtn.closest('.swiper-slide, .slick-slide') || specificThumbBtn;
      if (thumbWrapper.parentNode) thumbWrapper.parentNode.prepend(thumbWrapper);
      
      this.thumbItems = this.thumbItems.filter(i => i !== specificThumbBtn);
      this.thumbItems.unshift(specificThumbBtn);
    }

    // 3. Apply standard visibility filtering
    this.mainItems.forEach(item => {
      item.classList.remove('is-active');
      const isVisible = this._isVariantVisible(item, this.currentVariantId);
      item.style.setProperty('display', isVisible ? '' : 'none', 'important');
      item.classList.toggle('is-hidden-variant', !isVisible);
    });

    this.thumbItems.forEach(btn => {
      btn.classList.remove('is-active');
      const isVisible = this._isVariantVisible(btn, this.currentVariantId);
      
      const wrapper = btn.closest('.swiper-slide, .slick-slide') || btn;
      wrapper.style.setProperty('display', isVisible ? '' : 'none', 'important');
      btn.style.setProperty('display', isVisible ? '' : 'none', 'important');
      
      wrapper.classList.toggle('is-hidden-variant', !isVisible);
      btn.classList.toggle('is-hidden-variant', !isVisible);
    });

    const visibleItems = this.getVisibleItems();
    if (!visibleItems.length) return;

    // Update Counter Total
    if (this.counterTotal) {
      this.counterTotal.textContent = visibleItems.length;
    }

    // Because we moved the variant image to the front, index 0 is always correct
    this.showImage(0);
  }

  getVisibleItems() {
    return this.mainItems.filter(item => !item.classList.contains('is-hidden-variant'));
  }

  activateThumb(mediaId) {
    this.thumbItems.forEach(btn => btn.classList.remove('is-active'));
    const thumb = this.thumbLookup.get(String(mediaId));
    if (!thumb || thumb.classList.contains('is-hidden-variant')) return;
    thumb.classList.add('is-active');

    if (this.thumbGrid && this.thumbGrid.scrollWidth > this.thumbGrid.clientWidth) {
      const left = thumb.offsetLeft - (this.thumbGrid.clientWidth / 2) + (thumb.clientWidth / 2);
      this.thumbGrid.scrollTo({ left, behavior: 'smooth' });
    }
  }

  showImage(index) {
    const visibleItems = this.getVisibleItems();
    if (!visibleItems.length) return;

    this.activeIndex = (index < 0) ? visibleItems.length - 1 : (index >= visibleItems.length) ? 0 : index;

    this.mainItems.forEach(item => item.classList.remove('is-active'));
    
    const activeItem = visibleItems[this.activeIndex];
    activeItem.classList.add('is-active');

    this.activateThumb(activeItem.dataset.mediaId);

    if (this.counterCurrent) {
      this.counterCurrent.textContent = this.activeIndex + 1;
    }

    if (window.innerWidth <= 767 && this.layout === 'thumbnail_slider') {
      this.isScrolling = true;
      this.gallery.scrollTo({ left: activeItem.offsetLeft - this.gallery.offsetLeft, behavior: 'smooth' });
      
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = setTimeout(() => { this.isScrolling = false; }, 500);
    }
  }

  bindMobileScroll() {
    if (window.innerWidth >= 768) return;
    this.scrollObserver?.disconnect();

    this.scrollObserver = new IntersectionObserver((entries) => {
      if (this.isScrolling) return;

      entries.forEach(entry => {
        if (!entry.isIntersecting || entry.target.classList.contains('is-hidden-variant')) return;

        const index = this.getVisibleItems().indexOf(entry.target);
        if (index >= 0) {
          this.activeIndex = index;
          this.mainItems.forEach(item => item.classList.remove('is-active'));
          entry.target.classList.add('is-active');
          this.activateThumb(entry.target.dataset.mediaId);
          
          if (this.counterCurrent) {
            this.counterCurrent.textContent = index + 1;
          }
        }
      });
    }, { root: this.gallery, threshold: 0.6 });

    this.mainItems.forEach(item => this.scrollObserver.observe(item));
  }
}

function initDynamicMediaGallery() {
  document.querySelectorAll('.ceps-section-wrapper').forEach(wrapper => {
    if (!wrapper.dynamicGallery) {
      wrapper.dynamicGallery = new DynamicMediaGallery(wrapper);
    }
  });
}

['DOMContentLoaded', 'shopify:section:load', 'shopify:section:reorder', 'shopify:section:select']
  .forEach(evt => document.addEventListener(evt, initDynamicMediaGallery));